<template>
  <div>
    <v-toolbar dense color="grey lighten-3" flat>
      <v-icon>mdi-view-list</v-icon>
      <v-toolbar-title>Claims Status Cause</v-toolbar-title>
    </v-toolbar>
    <v-flex xs12>
      <message :messages="messages"></message>
    </v-flex>
    <v-card>
      <v-card-title>
        <v-spacer></v-spacer>
        <v-btn color="primary" dark class=" mr-2" @click="doNew">
          New Status Cause
          <v-icon right>mdi-plus</v-icon>
        </v-btn>
      </v-card-title>
      <v-flex xs12>
        <v-card>
          <v-data-table
            :headers="headers"
            :items="items"
            v-bind:options.sync="pagination"
            :server-items-length="totalItems"
            :loading="loading"
            :footer-props="{'items-per-page-options': itemsPerPage }"
            :no-data-text="noDataText"
          >
            <template v-slot:item="row"
              ><tr>
                <td class="text-xs-left">{{ row.item.claimStatusCause }}</td>
                <td class="text-xs-left">{{ row.item.claimStatusCauseDesc }}</td>
                <td class="table-action-header">
                  <table-action
                    :rowData="row"
                    review
                    @review="doReview(row.item)"
                    query
                    @query="doQuery(row.item)"
                    remove
                    @remove="doPromptRemove(row.item)"
                  ></table-action>
                </td></tr
            ></template>
          </v-data-table>
        </v-card>
      </v-flex>
    </v-card>
    <v-dialog v-model="claimStatusCauseReview" width="800px" persistent>
      <v-card>
        <v-toolbar v-if="reviewMode" dense color="grey lighten-3" flat>
          <v-icon>mdi-pencil</v-icon>
          <v-toolbar-title>Edit Status Cause </v-toolbar-title>
        </v-toolbar>
        <v-toolbar v-if="newMode" dense color="grey lighten-3" flat>
          <v-icon>mdi-plus</v-icon>
          <v-toolbar-title>Create Status Cause</v-toolbar-title>
        </v-toolbar>
        <v-toolbar v-if="queryMode" dense color="grey lighten-3" flat>
          <v-icon>mdi-magnify</v-icon>
          <v-toolbar-title>Query Status Cause</v-toolbar-title>
        </v-toolbar>
        <v-card-text>
          <message :messages="messages"></message>
          <v-container grid-list-md>
            <v-layout wrap>
              <v-flex xs12>
                <v-subheader class="queryLabel">{{ claimStatusCauseLabel.claimStatus }}</v-subheader>
                <div class="queryValue">{{ clmStat }} - {{ clmStatDesc }}</div>
              </v-flex>
              <v-flex xs6 v-if="newMode">
                <v-text-field
                  :label="claimStatusCauseLabel.claimStatusCause"
                  v-model="claimStatusCauseForm.claimStatusCause"
                  :error-messages="errors.collect('claimStatusCause')"
                  data-vv-name="claimStatusCause"
                  :data-vv-as="claimStatusCauseLabel.claimStatusCause"
                  v-validate="'required|param|max:8'"
                ></v-text-field>
              </v-flex>
              <v-flex xs6 v-else>
                <v-subheader class="queryLabel">{{ claimStatusCauseLabel.claimStatusCause }}</v-subheader>
                <div class="queryValue">{{ claimStatusCauseForm.claimStatusCause }}</div>
              </v-flex>
              <v-flex xs6 v-if="!queryMode">
                <v-text-field
                  :label="claimStatusCauseLabel.claimStatusCauseDesc"
                  v-model="claimStatusCauseForm.claimStatusCauseDesc"
                  :error-messages="errors.collect('claimStatusCauseDesc')"
                  data-vv-name="claimStatusCauseDesc"
                  :data-vv-as="claimStatusCauseLabel.claimStatusCauseDesc"
                  v-validate="'required|max:20'"
                ></v-text-field>
              </v-flex>
              <v-flex xs6 v-else>
                <v-subheader class="queryLabel">{{ claimStatusCauseLabel.claimStatusCauseDesc }}</v-subheader>
                <div class="queryValue">{{ claimStatusCauseForm.claimStatusCauseDesc }}</div>
              </v-flex>
            </v-layout>
            <v-layout row wrap>
              <v-flex xs6>
                <v-subheader class="queryLabel">{{ claimStatusCauseLabel.createdBy }}</v-subheader>
                <div class="queryValue">{{ claimStatusCauseForm.createdBy }}</div>
              </v-flex>
              <v-flex xs6 v-if="!newMode">
                <v-subheader class="queryLabel">{{ claimStatusCauseLabel.createdDate }}</v-subheader>
                <div class="queryValue">{{ claimStatusCauseForm.createdDate }}</div>
              </v-flex>
              <v-flex xs6 v-else>
                <v-subheader class="queryLabel">{{ claimStatusCauseLabel.createdDate }}</v-subheader>
                <div class="queryValue">{{ formatDisplayDate(claimStatusCauseForm.createdDate) }}</div>
              </v-flex>
            </v-layout>
            <v-layout row wrap v-if="!newMode">
              <v-flex xs6>
                <v-subheader class="queryLabel">{{ claimStatusCauseLabel.modifiedBy }}</v-subheader>
                <div class="queryValue">{{ claimStatusCauseForm.modifiedBy }}</div>
              </v-flex>
              <v-flex xs6>
                <v-subheader class="queryLabel">{{ claimStatusCauseLabel.modifiedDate }}</v-subheader>
                <div class="queryValue">{{ claimStatusCauseForm.modifiedDate }}</div>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="red darken-1" text @click.native="doClose">Cancel</v-btn>
          <v-btn color="primary" v-if="newMode || reviewMode" text @click.native="doSave">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <confirm v-model="promptRemove" @ok="doDelete">
      <p>Are you sure you want to delete the selected status cause?</p>
      <div class="queryValue">{{ selectedClaimStatusCause.claimStatusCause }} - {{ selectedClaimStatusCause.claimStatusCauseDesc }}</div>
    </confirm>
  </div>
</template>

<script>
import * as TableConst from '@/mixins/table'
import MessageMixin from '@/mixins/message'
import { expandURI, filterQuery } from '@/utils/ajax'
import { formatDisplayDate } from '@/utils/date'

export default {
  data() {
    return {
      toogleArray: [false],
      loading: false,
      headers: [{ text: 'Cause', value: 'claimStatusCause' }, { text: 'Description', value: 'claimStatusCauseDesc' }, TableConst.tableActionHeader],
      newMode: false,
      queryMode: false,
      reviewMode: false,
      claimStatusCauseReview: false,
      claimStatusCauseLabel: {
        claimStatus: 'Status',
        claimStatusCause: 'Cause',
        claimStatusCauseDesc: 'Description',
        createdBy: 'Created By',
        createdDate: 'Created Date',
        modifiedBy: 'Modified By',
        modifiedDate: 'Modified Date'
      },
      claimStatusCauseForm: {
        claimStatus: null,
        claimStatusDesc: null,
        claimStatusCause: null,
        claimStatusCauseDesc: null,
        createdBy: this.$store.state.token.firstName + ' ' + this.$store.state.token.lastName,
        createdDate: new Date(),
        modifiedBy: null,
        modifiedDate: null,
        mode: null
      },
      selectedClaimStatusCause: {
        claimStatusCause: null,
        claimStatusCauseDesc: null
      },
      promptRemove: false,
      totalItems: 0,
      filters: {
        claimStatus: undefined,
        claimStatusCause: undefined,
        claimStatusCauseDesc: undefined
      },
      pagination: {
        sortDesc: [false],
        page: 1
      }
    }
  },
  mounted() {
    this.doInit(true)
  },
  props: {
    clmStat: {
      type: String,
      default: null
    },
    clmStatDesc: {
      type: String,
      default: null
    }
  },
  methods: {
    formatDisplayDate(xDate) {
      return formatDisplayDate(xDate)
    },
    async doInit(options) {
      await Promise.all([this.$store.dispatch('claimStatusCauseManagementInit')])
      const apiURI = this.menu.claimStatusCauseList
      if (apiURI && this.clmStat) {
        this.loading = true
        try {
          this.loading = true
          const { page, itemsPerPage } = this.pagination
          let expand = {
            page: page - 1,
            size: itemsPerPage,
            options: options
          }
          this.filters.claimStatus = this.clmStat
          const result = await this.$http.get(filterQuery(expandURI(apiURI, expand), this.filters, this.pagination.sortBy, this.pagination.sortDesc))
          if (result) {
            this.totalItems = result.data.totalElements
            const { data } = result.data
            this.items = []
            if (data) {
              data.map((datamap) => {
                const convertList = {
                  claimStatus: datamap.clmStat,
                  claimStatusCause: datamap.clmStatCause,
                  claimStatusCauseDesc: datamap.clmStatCauseDesc
                }
                this.items.push(convertList)
              })
            }
          }
          this.loading = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
            this.fieldMessage(data)
          }
          this.loading = false
        }
      }
    },
    doReturn() {
      this.$router.push({
        name: 'clmStatReview',
        params: { clmStat: this.claimStatusCauseForm.claimStatus, mode: this.claimStatusCauseForm.claimStatusMode || 'query' }
      })
    },
    doNew() {
      this.resetValidator()
      this.resetMessages()
      this.claimStatusCauseForm.createdBy = this.$store.state.token.firstName + ' ' + this.$store.state.token.lastName
      this.claimStatusCauseForm.createdDate = new Date()
      this.claimStatusCauseForm.modifiedBy = null
      this.claimStatusCauseForm.modifiedDate = null
      this.claimStatusCauseForm.claimStatus = this.clmStat
      this.newMode = true
      this.reviewMode = false
      this.queryMode = false
      this.claimStatusCauseReview = true
    },
    async doReview(claimStatusCause) {
      this.doFetch(claimStatusCause)
      this.newMode = false
      this.reviewMode = true
      this.queryMode = false
    },
    async doQuery(claimStatusCause) {
      this.doFetch(claimStatusCause)
      this.newMode = false
      this.reviewMode = false
      this.queryMode = true
    },
    doFetch(claimStatusCause) {
      this.resetValidator()
      this.resetMessages()
      if (this.clmStat) {
        const expand = {
          claimStat: this.clmStat,
          claimStatCause: claimStatusCause.claimStatusCause
        }
        this.newMode = false
        const apiURI = this.menu.claimStatusCause
        if (apiURI) {
          this.loading = true
          this.$http
            .get(expandURI(apiURI, expand))
            .then((result) => {
              const { data } = result.data
              if (data) {
                this.claimStatusCauseForm.claimStatus = data.claimStatusCause.clmStat
                this.claimStatusCauseForm.claimStatusCause = data.claimStatusCause.clmStatCause
                this.claimStatusCauseForm.claimStatusCauseDesc = data.claimStatusCause.clmStatCauseDesc
                this.claimStatusCauseForm.createdBy = data.createdBy
                this.claimStatusCauseForm.createdDate = this.formatDisplayDate(data.createdDate)
                this.claimStatusCauseForm.modifiedBy = data.modifiedBy
                this.claimStatusCauseForm.modifiedDate = this.formatDisplayDate(data.modifiedDate)
                this.claimStatusCauseReview = true
              }
            })
            .catch((err) => {
              const { data } = err.response
              if (data) {
                this.message(data)
                this.fieldMessage(data)
              }
            })
        }
        this.loading = false
      }
    },
    async doPromptRemove(claimStatusCause) {
      this.resetMessages()
      if (claimStatusCause && claimStatusCause.claimStatusCause) {
        this.loading = true
        try {
          const apiURI = this.menu.checkDeleteClaimStatusCause
          const expand = {
            claimStatus: claimStatusCause.claimStatus,
            claimStatusCause: claimStatusCause.claimStatusCause
          }
          await this.$http.get(expandURI(apiURI, expand))
          this.selectedClaimStatusCause.claimStatus = claimStatusCause.claimStatus
          this.selectedClaimStatusCause.claimStatusCause = claimStatusCause.claimStatusCause
          this.selectedClaimStatusCause.claimStatusCauseDesc = claimStatusCause.claimStatusCauseDesc
          this.promptRemove = true
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.fieldMessage(data)
            this.notify(data)
          }
        }
        this.loading = false
      }
    },
    doClose() {
      this.resetValidator()
      this.resetMessages()
      this.claimStatusCauseForm.claimStatusCause = null
      this.claimStatusCauseForm.claimStatusCauseDesc = null
      this.claimStatusCauseReview = false
      setTimeout(() => {
        this.newMode = false
      }, 200)
    },
    doSave() {
      this.resetMessages()
      this.$validator.validateAll().then((result) => {
        if (result) {
          if (this.newMode) {
            this.doCreate()
          } else {
            this.doUpdate()
          }
        } else {
          this.promptFieldCheck()
        }
      })
    },
    doCreate() {
      const apiURI = this.menu.createClaimStatusCause
      if (apiURI) {
        const form = {
          claimStatus: this.claimStatusCauseForm.claimStatus,
          claimStatusCause: this.claimStatusCauseForm.claimStatusCause,
          claimStatusCauseDesc: this.claimStatusCauseForm.claimStatusCauseDesc
        }
        this.$http
          .post(apiURI, form)
          .then((result) => {
            const { data } = result
            if (data) {
              this.notify(data)
              this.doInit(true)
              this.doClose()
            }
          })
          .catch((err) => {
            const { data } = err.response
            if (data) {
              this.message(data)
              this.fieldMessage(data)
            }
          })
      }
    },
    doUpdate() {
      const apiURI = this.menu.updateClaimStatusCause
      if (apiURI) {
        const form = {
          claimStatus: this.claimStatusCauseForm.claimStatus,
          claimStatusCause: this.claimStatusCauseForm.claimStatusCause,
          claimStatusCauseDesc: this.claimStatusCauseForm.claimStatusCauseDesc
        }
        this.$http
          .put(apiURI, form)
          .then((result) => {
            const { data } = result
            if (data) {
              this.notify(data)
              this.doInit(true)
              this.doClose()
            }
          })
          .catch((err) => {
            const { data } = err.response
            if (data) {
              this.message(data)
              this.fieldMessage(data)
            }
          })
      }
    },
    doDelete() {
      this.resetMessages()
      if (this.selectedClaimStatusCause && this.selectedClaimStatusCause.claimStatusCause) {
        const apiURI = this.menu.deleteClaimStatusCause
        if (apiURI) {
          const expand = {
            claimStatus: this.selectedClaimStatusCause.claimStatus,
            claimStatusCause: this.selectedClaimStatusCause.claimStatusCause
          }
          this.$http
            .delete(expandURI(apiURI, expand))
            .then((result) => {
              const data = result.data
              this.notify(data)
              this.promptRemove = false
              this.doInit(true)
            })
            .catch((err) => {
              this.promptRemove = false
              const { data } = err.response
              if (data) {
                this.message(data)
              }
            })
        }
      }
    },
    doClearFilters() {
      this.resetMessages()
      this.filters.claimStatusCause = undefined
      this.filters.claimStatusCauseDesc = undefined
      this.pagination.page = 1
      this.doInit(false)
    },
    doFilters() {
      this.resetMessages()
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.pagination.page = 1
          this.doInit(false)
        }
      })
    }
  },
  watch: {
    'pagination': function (value) {
      this.doInit(false)
    }
  },
  computed: {
    menu() {
      return this.$store.state.claimStatusCauseParam.menu
    },
    clmStatus() {
      return this.$store.state.claimStatusParam.clmStatus
    }
  },
  mixins: [TableConst.default, MessageMixin]
}
</script>
<style>
</style>
